import { useState, useRef, useEffect } from 'react';

/**
 * Custom hook for managing signature canvas functionality
 * Handles canvas width calculation, signature state, and clearing
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.minWidth - Minimum canvas width (default: 280)
 * @param {number} options.maxWidth - Maximum canvas width (default: 512)
 * @param {number} options.height - Canvas height (default: 256)
 * @param {Function} options.onSignatureChange - Callback when signature changes
 * @returns {Object} - Signature canvas state and handlers
 */
export const useSignatureCanvas = ({
  minWidth = 280,
  maxWidth = 512,
  height = 256,
  onSignatureChange,
} = {}) => {
  const [canvasWidth, setCanvasWidth] = useState(minWidth);
  const [isSigned, setIsSigned] = useState(false);
  const signatureRef = useRef(null);
  const signatureContainerRef = useRef(null);

  // Update canvas width based on container size
  useEffect(() => {
    function updateCanvasWidth() {
      if (signatureContainerRef.current) {
        const containerWidth = signatureContainerRef.current.offsetWidth;
        const newWidth = Math.max(containerWidth, minWidth);
        setCanvasWidth(newWidth);
      }
    }

    updateCanvasWidth();
    window.addEventListener('resize', updateCanvasWidth);

    return () => {
      window.removeEventListener('resize', updateCanvasWidth);
    };
  }, [minWidth]);

  // Handle signature end event
  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setIsSigned(true);
      if (onSignatureChange) {
        onSignatureChange(true);
      }
    }
  };

  // Clear signature
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setIsSigned(false);
      if (onSignatureChange) {
        onSignatureChange(false);
      }
    }
  };

  // Get signature data URL
  const getSignatureData = () => {
    if (!signatureRef.current) {
      return null;
    }
    if (signatureRef.current.isEmpty()) {
      return null;
    }
    return signatureRef.current.getTrimmedCanvas().toDataURL('image/png');
  };

  // Validate signature exists
  const validateSignature = () => {
    if (!signatureRef.current) {
      return { valid: false, error: 'Signature component not ready' };
    }
    if (signatureRef.current.isEmpty()) {
      return { valid: false, error: 'Please provide your signature before submitting' };
    }
    return { valid: true };
  };

  // Signature canvas props (split for proper spreading)
  const signatureCanvasOptions = {
    penColor: 'black',
    backgroundColor: 'white',
    dotSize: 10,
    minWidth: 0.1,
    maxWidth: 10,
    throttle: 16,
    velocityFilterWeight: 0.7,
  };

  const signatureCanvasCanvasProps = {
    width: canvasWidth > maxWidth ? maxWidth : canvasWidth,
    height,
    style: {
      backgroundColor: 'white',
      touchAction: 'none',
      msTouchAction: 'none',
    },
  };

  return {
    signatureRef,
    signatureContainerRef,
    canvasWidth,
    isSigned,
    setIsSigned,
    clearSignature,
    getSignatureData,
    validateSignature,
    signatureCanvasOptions,
    signatureCanvasCanvasProps,
    onSignatureEnd: handleSignatureEnd,
  };
};
